import { useParams } from 'react-router-dom';
import {
  CampaignAdminModelMarketingStatus,
  CampaignAdminModelStage,
  useGetArchivedCampaignsByAdmin,
  useGetCampaignsForAgency,
} from '@urbanx/agx.campaigns.client';
import { useMemo } from 'react';
import useAgentsInAgency from './useAgentsInAgency';
import { CampaignsTab, LaunchFilterOptions, SearchableCampaign } from 'types/campaigns';
import {
  createAgentsNameLookup,
  CreateSearchableCampaigns,
  filterCampaigns,
} from 'utils/campaignUtils';

interface CampignsListFilters {
  currentTab: CampaignsTab;
  searchText?: string;
  statusFilter?: CampaignAdminModelStage[];
  agentsFilter?: string[];
  marketingFilter?: CampaignAdminModelMarketingStatus[];
  contractFilter?: string[];
  launchFilter?: LaunchFilterOptions;
}

const useCampaignsList = ({
  currentTab,
  searchText,
  statusFilter,
  agentsFilter,
  marketingFilter,
  contractFilter,
  launchFilter,
}: CampignsListFilters): {
  campaigns: SearchableCampaign[];
  archivedCampaigns: SearchableCampaign[];
  isLoadingCampaigns: boolean;
  isLoadingArchivedCampaigns: boolean;
} => {
  const { agencyId } = useParams();
  const { data: agents } = useAgentsInAgency();

  const { data: campaigns, isPending: isLoadingCampaigns } = useGetCampaignsForAgency(
    agencyId ?? '',
    !!agencyId && currentTab === CampaignsTab.All
  );

  const { data: archivedCampaigns, isPending: isLoadingArchivedCampaigns } =
    useGetArchivedCampaignsByAdmin(
      agencyId ?? '',
      !!agencyId && currentTab === CampaignsTab.Archived
    );

  const agentNamesLookup = useMemo(() => {
    return createAgentsNameLookup(agents || []);
  }, [agents]);

  const searchableCampaigns = useMemo(() => {
    return CreateSearchableCampaigns(campaigns ?? [], agentNamesLookup);
  }, [campaigns, agentNamesLookup]);

  const searchableArchivedCampaigns = useMemo(() => {
    return CreateSearchableCampaigns(archivedCampaigns ?? [], agentNamesLookup);
  }, [archivedCampaigns, agentNamesLookup]);

  const filteredCampaigns = useMemo(
    () =>
      filterCampaigns(
        searchableCampaigns,
        searchText,
        statusFilter,
        agentsFilter,
        marketingFilter,
        contractFilter,
        launchFilter
      ),
    [
      searchableCampaigns,
      searchText,
      statusFilter,
      agentsFilter,
      marketingFilter,
      contractFilter,
      launchFilter,
    ]
  );

  const filteredArchivedCampaigns = useMemo(
    () =>
      filterCampaigns(
        searchableArchivedCampaigns,
        searchText,
        statusFilter,
        agentsFilter,
        marketingFilter,
        contractFilter,
        launchFilter
      ),
    [
      searchableArchivedCampaigns,
      searchText,
      statusFilter,
      agentsFilter,
      marketingFilter,
      contractFilter,
      launchFilter,
    ]
  );

  return {
    campaigns: filteredCampaigns,
    archivedCampaigns: filteredArchivedCampaigns,
    isLoadingCampaigns,
    isLoadingArchivedCampaigns,
  };
};

export default useCampaignsList;
